import type {
  EnrollmentTokenResponse,
  Environment,
  ShipLevel,
  Topology,
  Visibility,
} from '@central/api';
import {
  Card,
  KpiTile,
  StatusChip,
  UptimeStrip,
  color,
  font,
  formatAgo,
  formatDate,
  type StatusTone,
  formatPercent,
  HealthChip,
  InfoRow,
} from '@central/ui';
import {
  Alert,
  Button,
  Code,
  CopyButton,
  Group,
  Modal,
  Radio,
  Select,
  Switch,
  Table,
  Tabs,
  TextInput,
} from '@mantine/core';
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useParams, useSearch } from '@tanstack/react-router';
import { useState, type FormEvent, type ReactNode } from 'react';
import { keys, useApi } from '../api';
import { LogSearch } from '../components/LogSearch';
import { Crumbs, DataTable, Empty, LoadMore, QueryState } from '../components/parts';
import {
  environmentTabs,
  logShippingChip,
  shipLevelLabel,
  shortKey,
  topologyLabel,
  type EnvironmentTab,
} from '../lib/environment';
import { enrollmentLabel, mono, muted } from '../lib/labels';
import { errorMessage } from '../lib/errors';
import { formatBytes } from '../lib/format';
import { useCanWrite } from '../lib/me';
import { FleetDetail, Metrics, Services } from './EnvironmentHealth';
import { Incidents, NextUpgrade } from './EnvironmentIncidents';

type VisibilityKey = keyof Visibility;

/** Visibility rows in design order. */
const visibilityRows: Array<{ key: VisibilityKey; label: string }> = [
  { key: 'moduleStatus', label: 'Status per module (Signals, Video…)' },
  { key: 'incidents', label: 'Incidents, in customer wording' },
  { key: 'versions', label: 'Installed version and releases' },
  { key: 'seatUsage', label: 'License seat usage (their tenant only)' },
  { key: 'serviceDetail', label: 'Services, nodes, databases, CPU and memory' },
  { key: 'logs', label: 'Logs' },
  { key: 'crashDiagnostics', label: 'Crash report diagnostics' },
  { key: 'agents', label: 'Communication agents (their tenant only)' },
];

/** Keys a shared environment always keeps off. */
const sharedLocked: VisibilityKey[] = ['serviceDetail', 'logs', 'crashDiagnostics'];

/** Environment detail (docs/design/AdminEnvironment.dc.html). */
export function EnvironmentDetail() {
  const api = useApi();
  const { environmentId } = useParams({ from: '/app/environments/$environmentId' });
  const env = useQuery({
    queryKey: keys.environment(environmentId),
    queryFn: () => api.environments.get(environmentId),
  });
  const e = env.data;
  const owner = e?.kind === 'dedicated' ? e.tenants[0] : undefined;
  return (
    <>
      <Crumbs
        items={[
          {
            label: owner ? 'Customers' : 'Environments',
            to: owner ? '/customers' : '/environments',
          },
          ...(owner
            ? [
                {
                  label: owner.customerName,
                  to: '/customers/$customerId',
                  params: { customerId: owner.customerId },
                },
              ]
            : []),
          { label: e?.name ?? '…' },
        ]}
      />
      <QueryState isPending={env.isPending} error={env.error}>
        {() => <Body e={e!} />}
      </QueryState>
    </>
  );
}

function Body({ e }: { e: Environment }) {
  const navigate = useNavigate({ from: '/environments/$environmentId' });
  const tab = useSearch({ from: '/app/environments/$environmentId' }).tab ?? 'health';
  const canWrite = useCanWrite();
  const [settings, setSettings] = useState(false);
  const [archiving, setArchiving] = useState(false);
  const customers = [...new Map(e.tenants.map((t) => [t.customerId, t.customerName])).values()];
  const licenseKeys = [...new Set(e.tenants.flatMap((t) => (t.licenseKey ? [t.licenseKey] : [])))];
  const meta: ReactNode[] = [
    e.kind === 'dedicated' ? 'Dedicated' : `Shared · ${customers.length} customers`,
    e.topology && topologyLabel[e.topology],
    !!e.nodeCount && `${e.nodeCount} ${e.nodeCount === 1 ? 'node' : 'nodes'}`,
    e.version && <span style={mono}>{e.version}</span>,
    `last heartbeat ${formatAgo(e.lastHeartbeatAt)}`,
    licenseKeys.length > 0 && (
      <>
        {licenseKeys.length === 1 ? 'license ' : 'licenses '}
        {licenseKeys.map((k, i) => (
          <span key={k}>
            {i > 0 && ', '}
            <span style={mono} title={k}>
              {shortKey(k)}
            </span>
          </span>
        ))}
      </>
    ),
    <Link key="audit" to="/audit" search={{ target: `environments/${e.id}` }}>
      Audit history
    </Link>,
  ].filter(Boolean);
  const panel = (children: ReactNode) => (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16, paddingTop: 16 }}>
      {children}
    </div>
  );
  return (
    <>
      <header
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          gap: 12,
        }}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6, minWidth: 0 }}>
          <Group gap={10} wrap="wrap">
            <h1 style={{ margin: 0, fontSize: 26, lineHeight: '34px' }}>{e.name}</h1>
            <HealthChip state={e.health} />
            {e.archivedAt && <StatusChip tone="unk">Archived</StatusChip>}
          </Group>
          {e.archivedAt && (
            <div style={{ fontSize: 14, ...muted }}>
              Archived {formatDate(e.archivedAt)}: hidden from lists, the overview, announcements
              and the portal.
            </div>
          )}
          <div style={{ fontSize: 14, ...muted }}>
            {meta.map((m, i) => (
              <span key={i}>
                {i > 0 && ' · '}
                {m}
              </span>
            ))}
          </div>
        </div>
        <Group gap={10}>
          <Button
            variant="default"
            h={40}
            renderRoot={(props) => (
              <Link
                {...props}
                to="/logs"
                search={{ customerId: e.tenants[0]?.customerId, environmentId: e.id }}
              />
            )}
          >
            Open logs
          </Button>
          {canWrite && (
            <Button variant="default" h={40} onClick={() => setSettings(true)}>
              Settings
            </Button>
          )}
          {canWrite && (
            <Button
              variant="default"
              h={40}
              disabled={!e.archivedAt && !archivable(e)}
              title={
                !e.archivedAt && !archivable(e)
                  ? 'Revoke the environment before archiving it'
                  : undefined
              }
              onClick={() => setArchiving(true)}
            >
              {e.archivedAt ? 'Unarchive' : 'Archive'}
            </Button>
          )}
        </Group>
      </header>
      {settings && <SettingsModal e={e} onClose={() => setSettings(false)} />}
      {archiving && <ArchiveModal e={e} onClose={() => setArchiving(false)} />}
      <Tabs
        value={tab}
        keepMounted={false}
        onChange={(v) =>
          void navigate({
            search: { tab: v === 'health' ? undefined : (v as EnvironmentTab) },
            replace: true,
          })
        }
      >
        <Tabs.List aria-label="Environment sections">
          {(Object.keys(environmentTabs) as EnvironmentTab[]).map((t) => (
            <Tabs.Tab key={t} value={t}>
              {environmentTabs[t]}
            </Tabs.Tab>
          ))}
        </Tabs.List>
        <Tabs.Panel value="health">
          {panel(
            <>
              <Uptime environmentId={e.id} />
              <Metrics e={e} />
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16, alignItems: 'flex-start' }}>
                <Services environmentId={e.id} style={{ flex: '2 1 480px' }} />
                <Enrollment e={e} />
              </div>
              <FleetDetail e={e} />
              <VisibilityCard e={e} />
            </>,
          )}
        </Tabs.Panel>
        <Tabs.Panel value="metrics">
          {panel(
            <>
              <Now e={e} />
              <Metrics e={e} />
            </>,
          )}
        </Tabs.Panel>
        <Tabs.Panel value="logs">
          {panel(
            <>
              <LogSearch environmentId={e.id} searchAtOnce idleHint="Choose filters and search." />
            </>,
          )}
        </Tabs.Panel>
        <Tabs.Panel value="versions">
          {panel(
            <>
              <NextUpgrade e={e} />
              <Versions e={e} />
            </>,
          )}
        </Tabs.Panel>
        <Tabs.Panel value="incidents">{panel(<Incidents e={e} />)}</Tabs.Panel>
        <Tabs.Panel value="enrollment">
          {panel(
            <>
              <Enrollment e={e} />
              <Tenants e={e} />
            </>,
          )}
        </Tabs.Panel>
      </Tabs>
    </>
  );
}

/** The latest report's summary: services up, cluster CPU and memory. */
function Now({ e }: { e: Environment }) {
  return (
    <Card title="Now">
      {e.summary ? (
        <>
          <InfoRow label="Services up">
            <span style={mono}>
              {e.summary.servicesUp} / {e.summary.servicesAll}
            </span>
          </InfoRow>
          {!!e.nodeCount && (
            <InfoRow label="Nodes">
              <span style={mono}>{e.nodeCount}</span>
            </InfoRow>
          )}
          <InfoRow label="Cluster CPU">
            <span style={mono}>{e.summary.cpuPercent.toFixed(0)}%</span>
          </InfoRow>
          <InfoRow label="Cluster memory">
            <span style={mono}>
              {formatBytes(e.summary.memUsedBytes)} / {formatBytes(e.summary.memTotalBytes)}
            </span>
          </InfoRow>
        </>
      ) : (
        <span style={{ fontSize: 14, ...muted }}>No heartbeat received yet.</span>
      )}
    </Card>
  );
}

/**
 * Rename the environment, set its topology, or set the lowest log level its
 * site ships; its kind follows its tenants.
 */
// Mounted only while open, so it starts from the latest stored e and every
// way out drops unsaved edits.
function SettingsModal({ e, onClose }: { e: Environment; onClose: () => void }) {
  const api = useApi();
  const queryClient = useQueryClient();
  const [name, setName] = useState(e.name);
  const [topology, setTopology] = useState<Topology | undefined>(e.topology);
  const [shipLevel, setShipLevel] = useState<ShipLevel>(e.logShipping.minLevel);
  const [nameError, setNameError] = useState<string>();
  const save = useMutation({
    mutationFn: () =>
      api.environments.update(e.id, {
        name: name.trim() !== e.name ? name.trim() : undefined,
        topology: topology !== e.topology ? topology : undefined,
        logShipLevel: shipLevel !== e.logShipping.minLevel ? shipLevel : undefined,
      }),
    onSuccess: async (next) => {
      queryClient.setQueryData(keys.environment(e.id), next);
      await queryClient.invalidateQueries({ queryKey: keys.environmentLists });
      await queryClient.invalidateQueries({ queryKey: keys.overview });
      onClose();
    },
  });
  const submit = (ev: FormEvent) => {
    ev.preventDefault();
    if (!name.trim()) return setNameError('Name the environment.');
    setNameError(undefined);
    if (name.trim() === e.name && topology === e.topology && shipLevel === e.logShipping.minLevel)
      return onClose();
    save.mutate();
  };
  return (
    <Modal opened onClose={onClose} title="Environment settings">
      <form
        onSubmit={submit}
        noValidate
        style={{ display: 'flex', flexDirection: 'column', gap: 14 }}
      >
        <TextInput
          label="Name"
          data-autofocus
          value={name}
          error={nameError}
          onChange={(ev) => setName(ev.currentTarget.value)}
        />
        <Select
          label="Topology"
          placeholder="Not reported"
          data={(Object.keys(topologyLabel) as Topology[]).map((t) => ({
            value: t,
            label: topologyLabel[t],
          }))}
          value={topology ?? null}
          allowDeselect={false}
          onChange={(v) => v && setTopology(v as Topology)}
        />
        <Select
          label="Ship logs"
          description="Lines below this level, and lines with no level, stay at the site."
          data={(Object.keys(shipLevelLabel) as ShipLevel[]).map((l) => ({
            value: l,
            label: shipLevelLabel[l],
          }))}
          value={shipLevel}
          allowDeselect={false}
          onChange={(v) => v && setShipLevel(v as ShipLevel)}
        />
        {save.error && <Alert color="red">{errorMessage(save.error)}</Alert>}
        <Group justify="flex-end">
          <Button variant="default" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" color="dark" loading={save.isPending}>
            Save
          </Button>
        </Group>
      </form>
    </Modal>
  );
}

/** The versions the environment has reported, newest first. */
function Versions({ e }: { e: Environment }) {
  const api = useApi();
  const versions = useInfiniteQuery({
    queryKey: keys.environmentVersions(e.id),
    queryFn: ({ pageParam }) => api.environments.versions(e.id, { cursor: pageParam }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.nextCursor,
  });
  const items = versions.data?.pages.flatMap((p) => p.items) ?? [];
  return (
    <Card
      title="Versions"
      padded={false}
      action={
        e.version && (
          <span style={{ fontSize: 13, ...muted }}>
            running <span style={mono}>{e.version}</span>
          </span>
        )
      }
    >
      <QueryState isPending={versions.isPending} error={versions.error}>
        {() =>
          items.length === 0 ? (
            <Empty>No version change recorded yet.</Empty>
          ) : (
            <DataTable label="Versions" head={['Version', 'Previous', 'First reported']}>
              {items.map((v) => (
                <Table.Tr key={`${v.version}-${v.at}`}>
                  <Table.Td style={{ ...mono, fontWeight: 500 }}>{v.version}</Table.Td>
                  <Table.Td style={mono}>{v.previous ?? '–'}</Table.Td>
                  <Table.Td>
                    {formatDate(v.at)}
                    <span style={{ fontSize: 13, ...muted }}> · {formatAgo(v.at)}</span>
                  </Table.Td>
                </Table.Tr>
              ))}
            </DataTable>
          )
        }
      </QueryState>
      {versions.hasNextPage && (
        <LoadMore
          what="versions"
          loading={versions.isFetchingNextPage}
          onClick={() => void versions.fetchNextPage()}
        />
      )}
    </Card>
  );
}

function Uptime({ environmentId }: { environmentId: string }) {
  const api = useApi();
  const uptime = useQuery({
    queryKey: keys.environmentUptime(environmentId),
    queryFn: () => api.environments.uptime(environmentId, 90),
  });
  const u = uptime.data?.uptime;
  const days = uptime.data?.days ?? [];
  const first = new Date(Date.now() - (days.length - 1) * 86_400_000).toISOString();
  return (
    <>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
          gap: 12,
        }}
      >
        <KpiTile
          label="Uptime · 24 h"
          value={
            <span style={{ fontFamily: font.mono, fontWeight: 500 }}>{formatPercent(u?.d1)}</span>
          }
        />
        <KpiTile
          label="Uptime · 7 d"
          value={
            <span style={{ fontFamily: font.mono, fontWeight: 500 }}>{formatPercent(u?.d7)}</span>
          }
        />
        <KpiTile
          label="Uptime · 30 d"
          value={
            <span style={{ fontFamily: font.mono, fontWeight: 500 }}>{formatPercent(u?.d30)}</span>
          }
        />
        <KpiTile
          label="Uptime · 90 d"
          value={
            <span style={{ fontFamily: font.mono, fontWeight: 500 }}>{formatPercent(u?.d90)}</span>
          }
        />
      </div>
      <Card
        title={`Last ${days.length || 90} days`}
        action={
          <span style={{ fontSize: 13, ...muted }}>
            A day is down when any core service is down 5 min or more
          </span>
        }
      >
        {uptime.isPending ? null : days.length === 0 ? (
          <span style={{ fontSize: 14, ...muted }}>
            {uptime.error ? errorMessage(uptime.error) : 'No uptime data yet.'}
          </span>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <UptimeStrip days={days} height={32} />
            <div
              style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, ...muted }}
            >
              <span>{formatDate(first)}</span>
              <span>Today</span>
            </div>
          </div>
        )}
      </Card>
    </>
  );
}

function Enrollment({ e }: { e: Environment }) {
  const api = useApi();
  const queryClient = useQueryClient();
  const canWrite = useCanWrite();
  // The token is shown once: held only in this component, never in the query cache.
  const [token, setToken] = useState<EnrollmentTokenResponse>();
  const issue = useMutation({
    mutationFn: () => api.environments.enrollmentToken(e.id),
    onSuccess: setToken,
  });
  const stored = async (next: Environment) => {
    queryClient.setQueryData(keys.environment(e.id), next);
    await queryClient.invalidateQueries({ queryKey: keys.environmentLists });
    await queryClient.invalidateQueries({ queryKey: keys.overview });
  };
  const approve = useMutation({
    mutationFn: () => api.environments.approve(e.id),
    onSuccess: stored,
  });
  const rotate = useMutation({
    mutationFn: () => api.environments.rotateKey(e.id),
    onSuccess: stored,
  });
  const [revoking, setRevoking] = useState(false);
  const en = e.enrollment;
  const tone: StatusTone =
    en.status === 'enrolled'
      ? 'ok'
      : en.status === 'pending_approval'
        ? 'warn'
        : en.status === 'revoked'
          ? 'down'
          : 'unk';

  return (
    <Card title="Enrollment" style={{ flex: '1 1 280px' }}>
      <InfoRow label="Status">
        <StatusChip tone={tone}>{enrollmentLabel[en.status]}</StatusChip>
      </InfoRow>
      {en.enrolledAt && <InfoRow label="Enrolled">{formatDate(en.enrolledAt)}</InfoRow>}
      {en.fingerprint && (
        <InfoRow label="Credential">
          <span style={mono}>{en.fingerprint}</span>
        </InfoRow>
      )}
      {en.kid && (
        <InfoRow label="Key id">
          <span style={mono}>{en.kid}</span>
        </InfoRow>
      )}
      {e.machineId && (
        <InfoRow label="Machine ID">
          <span style={mono}>{e.machineId}</span>
        </InfoRow>
      )}
      {en.credentialExpiresAt && (
        <InfoRow label="Credential expires">
          {formatDate(en.credentialExpiresAt)} ·{' '}
          {en.rotationRequestedAt ? 'rotation requested' : 'auto-rotates'}
        </InfoRow>
      )}
      {en.revokedAt && <InfoRow label="Revoked">{formatDate(en.revokedAt)}</InfoRow>}
      <InfoRow label="Log shipping">
        <Group gap={8} wrap="nowrap">
          <StatusChip tone={logShippingChip[e.logShipping.state].tone}>
            {logShippingChip[e.logShipping.state].label}
          </StatusChip>
          {e.logShipping.lastPushAt && (
            <span style={{ fontSize: 13, ...muted }}>{formatAgo(e.logShipping.lastPushAt)}</span>
          )}
          <span style={{ fontSize: 13, ...muted }}>{shipLevelLabel[e.logShipping.minLevel]}</span>
        </Group>
      </InfoRow>
      {canWrite && (en.status === 'enrolled' || en.status === 'pending_approval') && (
        <Group gap={8} mt={12}>
          {en.status === 'enrolled' && (
            <Button variant="default" loading={rotate.isPending} onClick={() => rotate.mutate()}>
              Rotate now
            </Button>
          )}
          <Button variant="default" color="red" onClick={() => setRevoking(true)}>
            Revoke
          </Button>
        </Group>
      )}
      {rotate.isSuccess && (
        <p style={{ fontSize: 13, margin: '8px 0 0', ...muted }}>
          The environment rotates its key on its next heartbeat.
        </p>
      )}
      {rotate.error && (
        <Alert mt={12} color="red">
          {errorMessage(rotate.error)}
        </Alert>
      )}
      <RevokeModal e={e} opened={revoking} onClose={() => setRevoking(false)} onRevoked={stored} />
      {en.status === 'pending_approval' && (
        <>
          <InfoRow label="Claimed license">
            <span style={mono}>{en.claimedLicenseKey ?? '–'}</span>
          </InfoRow>
          <InfoRow label="Claimed activation">
            <span style={mono}>{en.claimedActivationId ?? '–'}</span>
          </InfoRow>
          <p style={{ fontSize: 13, margin: '12px 0 0', ...muted }}>
            Enrolled with a license key. Approve once you have confirmed the installation is the
            customer’s.
          </p>
          {canWrite && (
            <Button
              mt={12}
              color="dark"
              loading={approve.isPending}
              onClick={() => approve.mutate()}
            >
              Approve
            </Button>
          )}
          {approve.error && (
            <Alert mt={12} color="red">
              {errorMessage(approve.error)}
            </Alert>
          )}
        </>
      )}
      {(en.status === 'not_enrolled' || en.status === 'revoked') && canWrite && e.archivedAt && (
        <p style={{ fontSize: 13, margin: '12px 0 0', ...muted }}>
          Archived environments cannot enroll. Unarchive it to issue an enrollment token.
        </p>
      )}
      {(en.status === 'not_enrolled' || en.status === 'revoked') && canWrite && !e.archivedAt && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginTop: 12 }}>
          {token ? (
            <div
              aria-label="Enrollment token"
              style={{ display: 'flex', flexDirection: 'column', gap: 8 }}
            >
              <span style={{ fontSize: 13, color: color.textBody }}>
                Enter this token in the GoATMS installer or License page. It is shown once, works
                once, and expires {formatDate(token.expiresAt)}.
              </span>
              <Code block style={{ wordBreak: 'break-all', whiteSpace: 'pre-wrap' }}>
                {token.token}
              </Code>
              <CopyButton value={token.token}>
                {({ copied, copy }) => (
                  <Button variant="default" size="xs" onClick={copy}>
                    {copied ? 'Copied' : 'Copy token'}
                  </Button>
                )}
              </CopyButton>
            </div>
          ) : (
            <span style={{ fontSize: 13, ...muted }}>
              Issue a one-time token to enroll this environment
              {en.status === 'revoked' ? ' again under a new key' : ''}. Issuing a new one
              invalidates any unused token.
            </span>
          )}
          <Button variant="default" loading={issue.isPending} onClick={() => issue.mutate()}>
            {token ? 'Issue a new token' : 'Create enrollment token'}
          </Button>
          {issue.error && <Alert color="red">{errorMessage(issue.error)}</Alert>}
        </div>
      )}
    </Card>
  );
}

/** Only an environment that holds no key, never enrolled or revoked, is archived. */
const archivable = (e: Environment) =>
  e.enrollment.status === 'not_enrolled' || e.enrollment.status === 'revoked';

/** Confirm archiving or unarchiving: an archived environment is hidden everywhere but here. */
function ArchiveModal({ e, onClose }: { e: Environment; onClose: () => void }) {
  const api = useApi();
  const queryClient = useQueryClient();
  const unarchive = !!e.archivedAt;
  const verb = unarchive ? 'Unarchive' : 'Archive';
  const run = useMutation({
    mutationFn: () =>
      unarchive ? api.environments.unarchive(e.id) : api.environments.archive(e.id),
    onSuccess: async (next) => {
      queryClient.setQueryData(keys.environment(e.id), next);
      await queryClient.invalidateQueries({ queryKey: keys.environmentLists });
      await queryClient.invalidateQueries({ queryKey: keys.overview });
      onClose();
    },
  });
  return (
    <Modal opened onClose={onClose} title={`${verb} ${e.name}`}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        <span style={{ fontSize: 14, color: color.textBody }}>
          {unarchive
            ? 'It returns to the environment lists, the overview, announcement audiences and its customers’ portal. It can then be given an enrollment token.'
            : 'It is hidden from the environment lists, the overview, announcement audiences and its customers’ portal. Its tenants, licenses, incidents and logs are kept, and it cannot enroll until it is unarchived.'}
        </span>
        {run.error && <Alert color="red">{errorMessage(run.error)}</Alert>}
        <Group justify="flex-end">
          <Button variant="default" onClick={onClose}>
            Cancel
          </Button>
          <Button color="dark" loading={run.isPending} onClick={() => run.mutate()}>
            {verb}
          </Button>
        </Group>
      </div>
    </Modal>
  );
}

/** Revoke with a reason: the key and log token stop working at once. */
function RevokeModal({
  e,
  opened,
  onClose,
  onRevoked,
}: {
  e: Environment;
  opened: boolean;
  onClose: () => void;
  onRevoked: (next: Environment) => Promise<void>;
}) {
  const api = useApi();
  const [reason, setReason] = useState('');
  const [reasonError, setReasonError] = useState<string>();
  const revoke = useMutation({
    mutationFn: () => api.environments.revoke(e.id, { reason: reason.trim() }),
    onSuccess: async (next) => {
      await onRevoked(next);
      close();
    },
  });
  // Every way out clears the form, so reopening never shows a stale reason or error.
  const close = () => {
    setReason('');
    setReasonError(undefined);
    revoke.reset();
    onClose();
  };
  const submit = (ev: FormEvent) => {
    ev.preventDefault();
    if (!reason.trim()) return setReasonError('Say why; it goes to the audit log.');
    setReasonError(undefined);
    revoke.mutate();
  };
  return (
    <Modal opened={opened} onClose={close} title={`Revoke ${e.name}`}>
      <form
        onSubmit={submit}
        noValidate
        style={{ display: 'flex', flexDirection: 'column', gap: 14 }}
      >
        <span style={{ fontSize: 14, color: color.textBody }}>
          Its key and log token stop working at once. Licenses and tenants are kept; GoATMS enters
          grace when its renewals stop. It can enroll again with a new token.
        </span>
        <TextInput
          label="Reason"
          data-autofocus
          value={reason}
          error={reasonError}
          onChange={(ev) => setReason(ev.currentTarget.value)}
        />
        {revoke.error && <Alert color="red">{errorMessage(revoke.error)}</Alert>}
        <Group justify="flex-end">
          <Button variant="default" onClick={close}>
            Cancel
          </Button>
          <Button type="submit" color="red" loading={revoke.isPending}>
            Revoke
          </Button>
        </Group>
      </form>
    </Modal>
  );
}

function Tenants({ e }: { e: Environment }) {
  if (e.tenants.length === 0) return null;
  return (
    <Card title="Tenants" padded={false}>
      <DataTable label="Tenants" head={['Tenant', 'Customer', 'License']} minWidth={480}>
        {e.tenants.map((t) => (
          <Table.Tr key={t.tenantId}>
            <Table.Td style={mono}>{t.tenantId}</Table.Td>
            <Table.Td>
              <Link to="/customers/$customerId" params={{ customerId: t.customerId }}>
                {t.customerName}
              </Link>
            </Table.Td>
            <Table.Td>
              {t.licenseId ? (
                <Link to="/licenses/$licenseId" params={{ licenseId: t.licenseId }}>
                  Open license
                </Link>
              ) : (
                <span style={muted}>Not bound</span>
              )}
            </Table.Td>
          </Table.Tr>
        ))}
      </DataTable>
    </Card>
  );
}

function VisibilityCard({ e }: { e: Environment }) {
  const api = useApi();
  const queryClient = useQueryClient();
  const canWrite = useCanWrite();
  const update = useMutation({
    mutationFn: (patch: { key: VisibilityKey; on: boolean }) =>
      api.environments.updateVisibility(e.id, { [patch.key]: patch.on }),
    onSuccess: (next) => queryClient.setQueryData(keys.environment(e.id), next),
  });
  const locked = new Set(e.lockedVisibility);
  const who =
    e.kind === 'dedicated' && e.tenants[0]
      ? `${e.tenants[0].customerName} sees it`
      : 'Customers see it';

  return (
    <Card padded={false}>
      <div
        style={{
          padding: '16px 20px',
          display: 'flex',
          flexWrap: 'wrap',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          gap: 16,
        }}
      >
        <div style={{ minWidth: 0, flex: '1 1 280px' }}>
          <h2 style={{ margin: 0, fontSize: 16 }}>What the customer can see</h2>
          <div style={{ fontSize: 13, ...muted }}>
            Set when the environment is created. Shared environments lock server detail, logs and
            diagnostics off.
          </div>
        </div>
        <Radio.Group
          label="Environment type"
          value={e.kind}
          styles={{ label: { fontSize: 13, fontWeight: 600 } }}
        >
          <Group gap={16} mt={6}>
            <Radio value="dedicated" label="Dedicated · one customer" disabled />
            <Radio value="shared" label="Shared · several customers" disabled />
          </Group>
        </Radio.Group>
      </div>
      {update.error && (
        <Alert color="red" mx={20} mb={12}>
          {errorMessage(update.error)}
        </Alert>
      )}
      <DataTable
        label="Customer visibility"
        head={['Item', who, 'On shared environments']}
        minWidth={560}
      >
        <Table.Tr>
          <Table.Td style={{ fontWeight: 500 }}>Overall health and uptime %</Table.Td>
          <Table.Td>Always shown</Table.Td>
          <Table.Td>
            <StatusChip tone="ok">Always on</StatusChip>
          </Table.Td>
        </Table.Tr>
        {visibilityRows.map((r) => {
          const isLocked = locked.has(r.key);
          const on = isLocked ? e.effectiveVisibility[r.key] : e.visibility[r.key];
          return (
            <Table.Tr key={r.key}>
              <Table.Td style={{ fontWeight: 500 }}>{r.label}</Table.Td>
              <Table.Td>
                <Switch
                  aria-label={r.label}
                  label={isLocked ? 'Locked off' : on ? 'Shown' : 'Hidden'}
                  checked={on}
                  disabled={isLocked || !canWrite || update.isPending}
                  onChange={(ev) => update.mutate({ key: r.key, on: ev.currentTarget.checked })}
                />
              </Table.Td>
              <Table.Td>
                {sharedLocked.includes(r.key) ? (
                  <StatusChip tone="down">Locked off</StatusChip>
                ) : (
                  <StatusChip tone="unk">Allowed</StatusChip>
                )}
              </Table.Td>
            </Table.Tr>
          );
        })}
      </DataTable>
    </Card>
  );
}
